import { useEffect, useState, useRef, useCallback } from 'react';
import { CalendarBlank, Trash, CaretLeft, CaretRight } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_SLOTS = [];
for (let h = 8; h <= 21; h++) {
  for (let m = 0; m < 60; m += 15) {
    if (h === 21 && m > 0) break;
    TIME_SLOTS.push({ h, m, label: `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}` });
  }
}

const WEEKDAYS = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const MONTHS = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function startOfWeek(d) {
  const day = d.getDay();
  const diff = day === 0 ? 6 : day - 1;
  const r = new Date(d);
  r.setDate(r.getDate() - diff);
  return r;
}
function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function slotIndex(timeStr) {
  return TIME_SLOTS.findIndex(s => s.label === timeStr);
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

export default function ScheduleCalendar({ studyId, studyColor, lang }) {
  const [visits, setVisits] = useState([]);
  const [selectedVisit, setSelectedVisit] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [allSlots, setAllSlots] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [studies, setStudies] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const [dragDay, setDragDay] = useState(null);
  const tableRef = useRef(null);

  const lb = lang === 'es'
    ? { title: 'Programacion de Visitas', select_visit: 'Seleccionar visita...', no_visits: 'Crea visitas primero en la pestana Visitas', today: 'Hoy', delete_visit_slots: 'Borrar programacion de esta visita', saved: 'Programacion guardada', deleted: 'Programacion eliminada', drag_hint: 'Pulsa y arrastra en el calendario para seleccionar franjas horarias', other_studies: 'Otros estudios', auto_date: 'Fecha calculada automaticamente a partir de' }
    : { title: 'Visit Scheduling', select_visit: 'Select visit...', no_visits: 'Create visits first in the Visits tab', today: 'Today', delete_visit_slots: 'Delete schedule for this visit', saved: 'Schedule saved', deleted: 'Schedule deleted', drag_hint: 'Click and drag on the calendar to select time slots', other_studies: 'Other studies', auto_date: 'Date auto-calculated from' };

  const loadData = useCallback(async () => {
    if (!studyId) return;
    try {
      const [vr, sr, ar, str, bks] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/schedule`, { withCredentials: true }),
        axios.get(`${API_URL}/api/schedule/all`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
        axios.get(`${API_URL}/api/schedule/blocks?study_id=${studyId}`, { withCredentials: true }),
      ]);
      setVisits(vr.data || []);
      setSlots(sr.data || []);
      setAllSlots(ar.data || []);
      setStudies(str.data || []);
      setBlocks(Array.isArray(bks.data) ? bks.data : []);
    } catch { }
  }, [studyId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Navigate to the first scheduled date when slots load
  useEffect(() => {
    if (slots.length > 0) {
      const sorted = [...slots].sort((a, b) => a.date.localeCompare(b.date));
      const firstDate = new Date(sorted[0].date + 'T00:00:00');
      setCurrentDate(firstDate);
    }
  }, [slots.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate target date for a visit based on another already-scheduled visit
  const getVisitTargetDate = useCallback((visitCode) => {
    if (!visitCode || visits.length === 0) return null;
    const visit = visits.find(v => v.code === visitCode);
    if (!visit) return null;
    // Anchor = visita ancla para calcular fechas. Preferencia:
    //   1) La propia "T0" si existe y esta programada
    //   2) Cualquier otra visita ya programada (la de menor `day`)
    //   3) Si ninguna esta programada, null (usuario programara la primera libremente)
    const t0 = visits.find(v => v.code === 'T0');
    let anchor = null;
    if (t0 && slots.some(s => s.visit_code === 'T0')) {
      anchor = t0;
    } else {
      const scheduledVisits = visits
        .filter(v => v.code !== visitCode && slots.some(s => s.visit_code === v.code))
        .sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
      anchor = scheduledVisits[0] || null;
    }
    if (!anchor || visitCode === anchor.code) return null;
    const anchorSlots = slots.filter(s => s.visit_code === anchor.code);
    if (anchorSlots.length === 0) return null;
    // Use the earliest scheduled date of the anchor visit as reference
    const anchorDate = anchorSlots.sort((a, b) => a.date.localeCompare(b.date))[0].date;
    // Offset relative to anchor visit
    const dayOffset = (visit.day ?? 0) - (anchor.day ?? 0);
    const baseDate = new Date(anchorDate + 'T00:00:00');
    const targetDate = addDays(baseDate, dayOffset);
    return targetDate;
  }, [visits, slots]);

  const handleSelectVisit = (v) => {
    const code = v === '_none' ? '' : v;
    setSelectedVisit(code);
    if (code) {
      const target = getVisitTargetDate(code);
      if (target) {
        setCurrentDate(target);
      }
    }
  };

  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const today = fmtDate(new Date());

  const navigate = (dir) => {
    setCurrentDate(prev => addDays(prev, dir * 7));
  };

  const handleMouseDown = (dayStr, slotLabel) => {
    if (!selectedVisit) { toast.error(lang === 'es' ? 'Selecciona una visita primero' : 'Select a visit first'); return; }
    setIsDragging(true);
    setDragDay(dayStr);
    setDragStart(slotLabel);
    setDragEnd(slotLabel);
  };

  const handleMouseMove = (dayStr, slotLabel) => {
    if (!isDragging || dayStr !== dragDay) return;
    setDragEnd(slotLabel);
  };

  const handleMouseUp = async () => {
    if (!isDragging || !dragStart || !dragEnd || !dragDay) { setIsDragging(false); return; }
    setIsDragging(false);
    const startIdx = slotIndex(dragStart);
    const endIdx = slotIndex(dragEnd);
    const fromIdx = Math.min(startIdx, endIdx);
    const toIdx = Math.max(startIdx, endIdx);
    // Cada celda del calendario representa 15 minutos. La duracion del hueco
    // viene dada por la propia visita (`duration_minutes`), normalmente 15 (default),
    // 5 (Qsalud/VAL/EA) u otro valor configurado para el estudio.
    const visitDef = visits.find(v => v.code === selectedVisit);
    const slotMinutes = Math.max(5, parseInt(visitDef?.duration_minutes, 10) || 15);
    const selectedMinutes = (toIdx - fromIdx + 1) * 15;
    // Generamos floor(selectedMinutes / slotMinutes) huecos consecutivos.
    // Convertimos a base de minutos para soportar slots de 5 min sin depender de TIME_SLOTS.
    const baseMinutes = TIME_SLOTS[fromIdx].h * 60 + TIME_SLOTS[fromIdx].m;
    const toHHMM = (mins) => {
      const hh = Math.floor(mins / 60);
      const mm = mins % 60;
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
    };
    const slotsToCreate = [];
    // URS-F472: creamos UN unico slot que abarque toda la seleccion, en
    // vez de N slots por hora / por duracion. La duracion de la visita se
    // conserva como granularidad (para redondear la seleccion) pero el
    // slot resultante engloba `count * slotMinutes` minutos como bloque
    // unico. Asi la Agenda muestra un solo hueco visual, el hover
    // (URS-F471) refleja el rango completo y las citas se van creando
    // dentro del bloque contando "citas cubiertas".
    const count = Math.floor(selectedMinutes / slotMinutes);
    if (count >= 1) {
      slotsToCreate.push({
        visit_code: selectedVisit,
        date: dragDay,
        start_time: toHHMM(baseMinutes),
        end_time: toHHMM(baseMinutes + count * slotMinutes),
      });
    }

    setDragStart(null);
    setDragEnd(null);
    setDragDay(null);

    if (slotsToCreate.length === 0) {
      toast.error(
        lang === 'es'
          ? `Selecciona al menos ${slotMinutes} minutos (duracion de un hueco)`
          : `Select at least ${slotMinutes} minutes (one slot duration)`,
      );
      return;
    }

    try {
      await axios.post(`${API_URL}/api/studies/${studyId}/schedule/batch`, {
        slots: slotsToCreate,
      }, { withCredentials: true });
      // URS-F472: siempre 1 slot; el toast informa del rango y de las
      // citas que pueden caber dentro (count = floor(rango / duracion)).
      const s0 = slotsToCreate[0];
      toast.success(
        lang === 'es'
          ? `Hueco ${s0.start_time}-${s0.end_time} creado (${count} ${count === 1 ? 'cita cabe' : 'citas caben'} dentro)`
          : `Slot ${s0.start_time}-${s0.end_time} created (${count} ${count === 1 ? 'appointment fits' : 'appointments fit'})`,
      );
      loadData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/schedule/${slotId}`, { withCredentials: true });
      toast.success(lb.deleted);
      loadData();
    } catch { toast.error('Error'); }
  };

  const handleDeleteVisitSlots = async () => {
    if (!selectedVisit) return;
    try {
      await axios.delete(`${API_URL}/api/studies/${studyId}/schedule?visit_code=${selectedVisit}`, { withCredentials: true });
      toast.success(lb.deleted);
      loadData();
    } catch { toast.error('Error'); }
  };

  const isInDragRange = (dayStr, slotLabel) => {
    if (!isDragging || dayStr !== dragDay) return false;
    const sIdx = slotIndex(dragStart);
    const eIdx = slotIndex(dragEnd);
    const cIdx = slotIndex(slotLabel);
    return cIdx >= Math.min(sIdx, eIdx) && cIdx <= Math.max(sIdx, eIdx);
  };

  const getSlotForCell = (dayStr, slotLabel) => {
    return slots.filter(s => s.date === dayStr && s.start_time <= slotLabel && s.end_time > slotLabel);
  };

  const getOtherStudySlots = (dayStr, slotLabel) => {
    return allSlots.filter(s => s.study_id !== studyId && s.date === dayStr && s.start_time <= slotLabel && s.end_time > slotLabel);
  };

  const getBlockForCell = (dayStr, slotLabel) => {
    return blocks.find(b => b.date === dayStr && b.start_time <= slotLabel && b.end_time > slotLabel);
  };

  const studyMap = {};
  studies.forEach(s => { studyMap[s.study_id] = s; });

  const visitSlots = selectedVisit ? slots.filter(s => s.visit_code === selectedVisit) : [];

  if (!studyId) return <p className="text-sm text-stone-500 text-center py-8">{lang === 'es' ? 'Guarda el estudio primero' : 'Save the study first'}</p>;

  return (
    <div className="space-y-4" data-testid="schedule-calendar">
      {/* Visit selector + controls */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-[200px] max-w-[350px]">
          <Select value={selectedVisit || '_none'} onValueChange={handleSelectVisit}>
            <SelectTrigger data-testid="schedule-visit-select" className="bg-white border-stone-200">
              <SelectValue placeholder={lb.select_visit} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">{lb.select_visit}</SelectItem>
              {visits.map(v => (
                <SelectItem key={v.visit_id} value={v.code}>
                  {v.code} — {v.name || `Dia ${v.day}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} className="h-8 w-8 rounded-sm">
            <CaretLeft size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())} className="rounded-sm text-xs">
            {lb.today}
          </Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} className="h-8 w-8 rounded-sm">
            <CaretRight size={16} />
          </Button>
          <span className="text-sm font-semibold text-stone-700 ml-2">
            {days[0].getDate()} - {days[6].getDate()} {MONTHS[days[6].getMonth()]} {days[6].getFullYear()}
          </span>
        </div>

        {selectedVisit && visitSlots.length > 0 && (
          <Button variant="outline" size="sm" onClick={handleDeleteVisitSlots} className="text-red-600 border-red-200 hover:border-red-300 rounded-sm gap-1 ml-auto">
            <Trash size={14} />{lb.delete_visit_slots}
          </Button>
        )}
      </div>

      {visits.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-12 text-center">
          <CalendarBlank size={40} className="text-stone-300 mx-auto mb-3" />
          <p className="text-sm text-stone-500">{lb.no_visits}</p>
        </div>
      ) : (
        <>
          {(() => {
            // URS-F190: Live preview del numero de huecos durante el drag.
            if (!isDragging || !dragStart || !dragEnd || !dragDay) {
              return <p className="text-xs text-stone-400 italic">{lb.drag_hint}</p>;
            }
            const sIdx = slotIndex(dragStart);
            const eIdx = slotIndex(dragEnd);
            const cells = Math.abs(eIdx - sIdx) + 1;
            const totalMin = cells * 15;
            const visitDef = visits.find(v => v.code === selectedVisit);
            const slotMin = Math.max(5, parseInt(visitDef?.duration_minutes, 10) || 15);
            // URS-F472: el drag crea UN unico slot. `capacity` = cuantas
            // citas de `slotMin` minutos cabran dentro del bloque.
            const capacity = Math.floor(totalMin / slotMin);
            const ok = capacity > 0;
            const blockMin = capacity * slotMin;
            const text = lang === 'es'
              ? (ok
                  ? `Hueco de ${blockMin} min (${capacity} ${capacity === 1 ? 'cita cabe' : 'citas caben'} de ${slotMin} min)`
                  : `Selecciona al menos ${slotMin} min`)
              : (ok
                  ? `${blockMin} min slot (${capacity} ${capacity === 1 ? 'appt fits' : 'appts fit'} of ${slotMin} min)`
                  : `Select at least ${slotMin} min`);
            return (
              <p
                className={`text-xs font-semibold ${ok ? 'text-[#1f3e80]' : 'text-amber-600'}`}
                data-testid="schedule-drag-preview"
              >
                {text}
              </p>
            );
          })()}
          {selectedVisit && getVisitTargetDate(selectedVisit) && (() => {
            const target = getVisitTargetDate(selectedVisit);
            // Mismo criterio que getVisitTargetDate: T0 preferente, si no, primera ya programada
            const t0 = visits.find(v => v.code === 'T0');
            let anchor = null;
            if (t0 && slots.some(s => s.visit_code === 'T0')) {
              anchor = t0;
            } else {
              const scheduledVisits = visits
                .filter(v => v.code !== selectedVisit && slots.some(s => s.visit_code === v.code))
                .sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
              anchor = scheduledVisits[0] || null;
            }
            return (
              <p className="text-xs text-[#1f3e80] font-medium">
                {lb.auto_date} {anchor?.code}: {target.toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            );
          })()}

          {/* Legend for other studies */}
          {(() => {
            const otherStudies = studies.filter(s => s.study_id !== studyId && s.status === 'activo' && (s.visit_count || 0) > 0);
            return otherStudies.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs text-stone-500 font-medium">{lb.other_studies}:</span>
              {otherStudies.map(s => (
                <div key={s.study_id} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: hexToRgba(s.color || '#888', 0.4) }} />
                  <span className="text-xs text-stone-500">{s.code}</span>
                </div>
              ))}
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: hexToRgba(studyColor || '#1f3e80', 0.5) }} />
                <span className="text-xs text-stone-700 font-semibold">{lang === 'es' ? 'Este estudio' : 'This study'}</span>
              </div>
            </div>
          );})()}

          {/* Calendar grid */}
          <div className="bg-white border border-stone-200 rounded-sm overflow-hidden select-none" ref={tableRef}
            onMouseUp={handleMouseUp} onMouseLeave={() => { if (isDragging) handleMouseUp(); }}>
            {/* Header */}
            <div className="grid grid-cols-[56px_repeat(7,1fr)] border-b border-stone-200 bg-stone-50">
              <div className="p-2" />
              {days.map((d, i) => {
                const ds = fmtDate(d);
                return (
                  <div key={i} className={`p-2 text-center border-l border-stone-200 ${ds === today ? 'bg-[#1f3e80]/5' : ''}`}>
                    <p className="text-xs text-stone-500">{WEEKDAYS[i]}</p>
                    <p className={`text-sm font-semibold ${ds === today ? 'text-[#1f3e80]' : 'text-stone-800'}`}>{d.getDate()}</p>
                  </div>
                );
              })}
            </div>
            {/* Time grid */}
            <div className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
              {TIME_SLOTS.map(slot => {
                const isHour = slot.m === 0;
                return (
                  <div key={slot.label} className={`grid grid-cols-[56px_repeat(7,1fr)] ${isHour ? 'min-h-[28px]' : 'min-h-[24px]'}`}>
                    <div className={`px-1 border-r border-stone-100 text-right ${isHour ? 'py-1 text-xs font-mono text-stone-400' : 'py-0.5 text-[10px] font-mono text-stone-300'}`}>
                      {slot.label}
                    </div>
                    {days.map((d, i) => {
                      const ds = fmtDate(d);
                      const inDrag = isInDragRange(ds, slot.label);
                      const mySlots = getSlotForCell(ds, slot.label);
                      const otherSlots = getOtherStudySlots(ds, slot.label);
                      const block = getBlockForCell(ds, slot.label);
                      const isFirstOfSlot = mySlots.some(s => s.start_time === slot.label);
                      const isFirstOfBlock = block && block.start_time === slot.label;

                      // Collect all studies with slots in this cell for side-by-side rendering
                      const allCellStudies = [];
                      mySlots.forEach(s => { if (!allCellStudies.find(x => x.study_id === s.study_id)) allCellStudies.push(s); });
                      otherSlots.forEach(s => { if (!allCellStudies.find(x => x.study_id === s.study_id)) allCellStudies.push(s); });

                      return (
                        <div key={i}
                          className="border-l border-stone-100 relative cursor-crosshair transition-colors"
                          style={inDrag ? { backgroundColor: hexToRgba(studyColor || '#1f3e80', 0.3) } : {}}
                          onMouseDown={() => handleMouseDown(ds, slot.label)}
                          onMouseMove={() => handleMouseMove(ds, slot.label)}>
                          {/* Side-by-side study stripes */}
                          {!inDrag && allCellStudies.length > 0 && (
                            <div className="absolute inset-0 flex pointer-events-none">
                              {allCellStudies.map((cs, ci) => {
                                const isMine = cs.study_id === studyId;
                                const oStudy = studyMap[cs.study_id];
                                const color = isMine ? (studyColor || '#1f3e80') : (oStudy?.color || '#888');
                                return (
                                  <div key={ci} style={{
                                    flex: 1,
                                    backgroundColor: hexToRgba(color, isMine ? 0.18 : 0.12),
                                    borderLeft: `2px solid ${hexToRgba(color, 0.5)}`,
                                  }} />
                                );
                              })}
                            </div>
                          )}
                          {/* Show visit code label at start of our slot */}
                          {isFirstOfSlot && mySlots.map(ms => (
                            <div key={ms.slot_id} className="absolute top-0 left-0 right-0 flex items-center justify-between px-1 z-10">
                              <span className="text-[9px] font-semibold truncate" style={{ color: studyColor || '#1f3e80' }}>
                                {ms.visit_code} {ms.start_time}-{ms.end_time}
                              </span>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteSlot(ms.slot_id); }}
                                className="text-red-400 hover:text-red-600 p-0 leading-none">
                                <Trash size={10} />
                              </button>
                            </div>
                          ))}
                          {/* Block overlay (red hachure) */}
                          {block && (
                            <div
                              className="absolute inset-0 pointer-events-none"
                              style={{
                                backgroundColor: '#dc2626',
                                opacity: 0.22,
                                backgroundImage: 'repeating-linear-gradient(45deg, transparent 0 4px, rgba(255,255,255,0.45) 4px 8px)',
                                border: '1px solid rgba(220,38,38,0.5)',
                                zIndex: 5,
                              }}
                              title={`Bloqueo: ${block.reason || ''}`}
                            />
                          )}
                          {isFirstOfBlock && (
                            <div className="absolute top-0 left-0 right-0 px-1 text-[9px] text-red-700 font-semibold truncate pointer-events-none z-20" title={block.reason}>
                              ⛔ {block.reason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary of scheduled slots */}
          {visitSlots.length > 0 && selectedVisit && (
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
              <p className="text-xs font-semibold text-stone-600 mb-2">
                {selectedVisit} — {visitSlots.length} {lang === 'es' ? 'franjas programadas' : 'scheduled slots'}
              </p>
              <div className="flex flex-wrap gap-2">
                {visitSlots.sort((a, b) => `${a.date}${a.start_time}`.localeCompare(`${b.date}${b.start_time}`)).map(s => (
                  <Badge key={s.slot_id} variant="outline" className="text-xs gap-1 cursor-pointer hover:bg-red-50"
                    onClick={() => handleDeleteSlot(s.slot_id)}>
                    {s.date} {s.start_time}-{s.end_time}
                    <Trash size={10} className="text-red-400" />
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
